import React, { useEffect, useState } from "react";
import { Tabs, message, Spin } from "antd";
import PageWrapper from "../../../Layout/PageWrapper";
import { CreatorProfileData, CreatorProfileService } from "../../../services/Invoices/CreatorProfile/CreatorProfileService";
import BankDetails, { BankFormValues } from "./BankDetails/BankDetails";
import CompanyDetails, { FormValues } from "./CompanyDetails/CompanyDetails";
import InvoiceDefaults, { InvoiceFormValues } from "./InvoiceDefaults/InvoiceDefaults";
import { useParams } from "react-router-dom";

const { TabPane } = Tabs;
export const reverseFormatPhone = (formattedPhone: string): string => {
	if (!formattedPhone) return "";

	const cleaned = formattedPhone.replaceAll(" ", "");
	const [countryCode, number] = cleaned.split("-");

	if (!countryCode || !number) return formattedPhone;

	const normalizedCountryCode = countryCode.replace(/^0+/, "");

	return `+${normalizedCountryCode}${number}`;
};

const InvoiceCreatorProfile: React.FC = () => {
	const [loadingTab, setLoadingTab] = useState<string | null>(null);
	const [profile, setProfile] = useState<CreatorProfileData | null>(null);
	const [activeTab, setActiveTab] = useState("0");
	const [currencyCountryId, setCurrencyCountryId] = useState<number>();

	const { id } = useParams<{ id: string }>();
	useEffect(() => {
		if (!id) return;
		getProfileData(id);
	}, []);

	const getProfileData = async (id: string) => {
		setLoadingTab("0");
		try {
			const res = await CreatorProfileService.getProfile(id);
			const updatedProfile = {
				...res.data,
				phoneNumber: reverseFormatPhone(res.data.phoneNumber)
			};
			setProfile(updatedProfile);
			setCurrencyCountryId(res.data?.countryId);
			let step = res.data?.stepCompleted || 0;
			if (step == 3) {
				step = 0;
			}
			setActiveTab(step.toString());
		} catch (error: any) {
			if (error.status === 404) return;
			else if (error.status === 429) message.error("Too many requests. Please try again later.");
			else message.error(error?.response?.data?.message || error?.response || error);
		} finally {
			setLoadingTab(null);
		}
	};

	const saveCompanyDetails = async (values: FormValues) => {
		setLoadingTab("0");
		try {
			const response = await CreatorProfileService.submitProfileStep1(values,id);
			setCurrencyCountryId(values.countryId);
			message.success("Company details saved!");
			setActiveTab("1");
		} catch (error: any) {
			if (error.status === 429) message.error("Too many requests. Please try again later.");
			else message.error(error.response?.data?.message || "Failed to save company details");
		} finally {
			setLoadingTab(null);
		}
	};

	const saveInvoiceDefaults = async (values: InvoiceFormValues) => {
		setLoadingTab("1");
		try {
			const response = await CreatorProfileService.submitProfileStep2(values,id);
			message.success("Invoice defaults saved!");
			setActiveTab("2");
		} catch (error: any) {
			if (error.status === 429) message.error("Too many requests. Please try again later.");
			else message.error(error.response?.data?.message || "Failed to save invoice defaults");
		} finally {
			setLoadingTab(null);
		}
	};

	const saveBankDetails = async (values: BankFormValues) => {
		setLoadingTab("2");
		try {
			const response = await CreatorProfileService.submitProfileStep3(values,id);
			message.success("Bank details saved!");
		} catch (error: any) {
			if (error.status === 429) message.error("Too many requests. Please try again later.");
			else message.error(error.response?.data?.message || "Failed to save bank details");
		} finally {
			setLoadingTab(null);
		}
	};

	return (
		<PageWrapper title="Business Profile">
			<div className="max-w-5xl mx-auto bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-2xl shadow-2xl p-6 md:p-8 mt-8">
				<Tabs centered size="large" tabBarGutter={60} activeKey={activeTab} onChange={(k) => setActiveTab(k)}>
					<TabPane tab="Company Details" key="0">
						<Spin spinning={loadingTab === "0"}>
							<CompanyDetails initialValues={profile ? profile : null} onSave={saveCompanyDetails} loading={loadingTab === "0"} userId={id}/>
						</Spin>
					</TabPane>

					<TabPane tab="Invoice Defaults" key="1">
						<Spin spinning={loadingTab === "1"}>
							<InvoiceDefaults
								initialValues={profile?.invoiceDefaultResponse}
								currencyCountryId={currencyCountryId}
								onSave={saveInvoiceDefaults}
								loading={loadingTab === "1"}
							/>
						</Spin>
					</TabPane>

					<TabPane tab="Payout Method" key="2">
						<Spin spinning={loadingTab === "2"}>
							<BankDetails
										initialValues={profile?.bankDetails}
										countryCode={profile?.countryCode|| currencyCountryId ==6? "IND":""}
										onSave={saveBankDetails}
										loading={loadingTab === "2"}
									/>
						</Spin>
					</TabPane>
				</Tabs>
			</div>
		</PageWrapper>
	);
};

export default InvoiceCreatorProfile;
